<script lang="ts">
  import type { Atelier } from '../../stores/creation.svelte'

  let { atelier }: { atelier: Atelier } = $props()

  const noms = $derived(atelier.referentiel.categories_armes)
  const ouverts = $derived(atelier.etat.dotations_ouvertes)

  /**
   * Le libellé d'un catalogue ouvert, faute de nom : c'est un identifiant de
   * règle, pas un objet, donc il n'a pas de nom au référentiel.
   */
  function lisible(id: string): string {
    const mot = id.replace(/_/g, ' ')
    return mot.charAt(0).toUpperCase() + mot.slice(1)
  }
</script>

<p class="chapo">
  Le Rang 1 de la Classe fournit l'équipement de départ. Certaines lignes
  laissent un choix : tant qu'il n'est pas tranché, la Création ne peut pas
  aboutir — c'est une dépense au même titre que les points.
</p>

{#if !atelier.feuille.classe}
  <p class="note">Choisissez d'abord une Classe.</p>
{/if}

{#each ouverts as choix (choix.noeud + choix.index)}
  <section class="cartouche choix">
    <div class="bandeau">
      {#if choix.forme === 'maitrise'}
        {choix.nombre} Maîtrise{choix.nombre > 1 ? 's' : ''} supplémentaire{choix.nombre > 1 ? 's' : ''} au choix
      {:else if choix.forme === 'amelioration_de_maitrise'}
        {choix.nombre} Amélioration{choix.nombre > 1 ? 's' : ''} de Maîtrise
      {:else if choix.forme === 'catalogue'}
        {choix.nombre} × {lisible(choix.catalogue ?? '')}
      {:else if choix.forme === 'budget'}
        {choix.nombre} armes pour {choix.budget_livres} £ au total
      {:else}
        Une des deux options
      {/if}
    </div>
    <div class="corps">

    {#if choix.forme === 'maitrise'}
      <div class="options">
        {#each atelier.etat.maitrises_offertes as id (id)}
          {@const arbitrage = atelier.feuille.dotations.find(
            (d) => d.noeud === choix.noeud && d.index === choix.index,
          )}
          {@const prise = arbitrage?.maitrises?.includes(id) ?? false}
          <button
            type="button"
            class="option"
            class:active={prise}
            onclick={() =>
              atelier.trancher(choix.noeud, choix.index, { maitrises: prise ? [] : [id] })}
          >
            {noms[id] ?? id}
          </button>
        {/each}
      </div>
    {:else if choix.forme === 'alternative'}
      <div class="options">
        {#each choix.options as option, i (i)}
          <button
            type="button"
            class="option"
            onclick={() => atelier.trancher(choix.noeud, choix.index, { option: i })}
          >
            {option}
          </button>
        {/each}
      </div>
    {:else}
      <p class="arbitrage">
        Ce choix se fait à la table : le livre ouvre un catalogue que
        l'application ne borne pas encore.
      </p>
    {/if}
    </div>
  </section>
{/each}

{#if atelier.etat.inventaire.length > 0}
  <div class="cartouche">
    <div class="bandeau">Inventaire de départ</div>
    <div class="corps">
      <ul class="inventaire">
        {#each atelier.etat.inventaire as ligne (ligne.id + ligne.source)}
          <li>
            <span>{ligne.nom ?? lisible(ligne.id)}</span>
            {#if ligne.quantite > 1}<span class="quantite">× {ligne.quantite}</span>{/if}
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

{#if atelier.etat.a_arbitrer.length > 0}
  <div class="cartouche">
    <div class="bandeau">À arbitrer à la table</div>
    <div class="corps">
      <ul class="arbitrer">
        {#each atelier.etat.a_arbitrer as ligne, i (ligne + i)}
          <li>{ligne}</li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

{#if atelier.etat.argent !== null}
  <p class="argent">Argent de départ : <strong>{atelier.etat.argent} £</strong></p>
{/if}

<style>
  .chapo {
    color: var(--ink-soft);
    max-width: 62ch;
    margin: 0 0 calc(var(--pas) * 4);
  }
  .note {
    color: var(--gold);
  }
  .choix {
    background: var(--surface);
    border: 1px solid var(--line);
    border-left: 3px solid var(--accent);
    border-radius: var(--r);
    padding: calc(var(--pas) * 3);
    margin-bottom: calc(var(--pas) * 3);
  }
  .options {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
    gap: var(--pas);
  }
  .option {
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: calc(var(--pas) * 2);
    color: var(--ink);
    cursor: pointer;
    font: inherit;
    font-size: 0.85rem;
    text-align: left;
  }
  .option.active {
    border-color: var(--accent);
    color: var(--gold);
  }
  .arbitrage {
    font-size: 0.85rem;
    color: var(--ink-soft);
    margin: 0;
  }
  .inventaire,
  .arbitrer {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 1px;
  }
  .inventaire li,
  .arbitrer li {
    display: flex;
    justify-content: space-between;
    gap: var(--pas);
    background: var(--surface);
    padding: calc(var(--pas) * 2) calc(var(--pas) * 3);
    font-size: 0.9rem;
  }
  .arbitrer li {
    color: var(--gold);
    border-left: 2px solid var(--gold);
  }
  .quantite {
    color: var(--gold);
    font-variant-numeric: tabular-nums;
  }
  .argent {
    margin-top: calc(var(--pas) * 4);
    font-size: 0.95rem;
  }
  .argent strong {
    color: var(--gold);
    font-family: 'Cinzel', serif;
  }
</style>
